import { StorageError } from '../../app/errors';
import { requireTransaction } from '../commit';
import { db, TABLE } from '../db';
import type { OutboxRow } from '../../types';

/**
 * SYN-1's outbox — the push *intent*, written in the same transaction as the state transition.
 *
 * ENV-9 is the whole reason this table exists: *"the network is never on the critical path of
 * capture … no state transition may block on, await, or fail due to a network operation."* The
 * transition writes a row and commits; U-18's drainer performs the PUT afterwards, fire-and-forget.
 * Nothing in this file touches the network, and nothing may be added here that does.
 *
 * ── `put`, and why the primary key must stay compound ────────────────────────
 *
 * `[kind+scopeId]` is the **primary key**, not a unique index over a surrogate id, so `put` is a
 * true upsert and re-triggering **coalesces** (SYN-10). Design §3.4 spells out what a surrogate id
 * would cost: a fresh ULID against a merely-unique `&[kind+scopeId]` still throws `ConstraintError`,
 * and because this row shares the transition's transaction, that error would **abort the Half or
 * Full Time transition itself** whenever a prior push had not yet drained — the exact failure the
 * outbox exists to prevent. Do not normalize this.
 */

/**
 * Enqueues or refreshes the push intent for one scope.
 *
 * Counters reset on every trigger. That is coalescing's actual semantics here: the row records
 * "this scope needs pushing, most recently because of X", not a history of attempts — a second Half
 * tap after a failed drain should be retried as a fresh intent, not as attempt 4 of an old one.
 *
 * @throws {StorageError} outside a transaction, or on a write failure.
 */
export async function enqueue(row: OutboxRow): Promise<void> {
  requireTransaction('outbox.enqueue');
  try {
    await db().outbox.put(row);
  } catch (cause) {
    throw new StorageError('Could not record the backup push.', {
      cause,
      context: { store: TABLE.outbox, kind: row.kind, scopeId: row.scopeId },
    });
  }
}

/**
 * Every queued row, oldest trigger first.
 *
 * U-18 owns the drain; this is the read that makes coalescing observable — a test asserting "two
 * triggers leave exactly one row" needs to count them, and counting through a Dexie handle in the
 * test would prove something about the test rather than about this module.
 *
 * @throws {StorageError} on a read failure.
 */
export async function list(): Promise<OutboxRow[]> {
  try {
    return await db().outbox.orderBy('enqueuedAt').toArray();
  } catch (cause) {
    throw new StorageError('Could not read the backup queue.', {
      cause,
      context: { store: TABLE.outbox },
    });
  }
}
